import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { t } from "@/i18n-js/instance";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { AlreadyHaveAccountLink } from "@circle-react/components/OnboardingComponents/AlreadyHaveAccountLink";
import { BackToOptionsLink } from "@circle-react/components/OnboardingComponents/BackToOptionsLink";
import { LoginOptions } from "@circle-react/components/OnboardingComponents/LoginOptions";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { OnboardingLayout } from "@circle-react/components/OnboardingPage/layouts/OnboardingLayout";
import { PageTitle } from "@circle-react/components/OnboardingPage/layouts/PageTitle";
import { useSetCouponCodeFromParamsToCookie } from "@circle-react/hooks/useSetCouponCodeFromParamsToCookie";
import { useOnboardingWhiteLabel } from "../hooks/useOnboardingWhiteLabel";
import { SignUpForm } from "./SignUpForm";

export const CreateProfilePage = () => {
  useSetCouponCodeFromParamsToCookie();
  const [shouldShowForm, setShouldShowForm] = useState(false);
  const { currentCommunity, invitationToken, errors, promotion_details } =
    useCurrentCommunityContext();

  const currentHash = window.location.hash;
  const localeScope = "authentication_app.signup";
  const urlParams = useRouterQueryParams();
  const couponCode = urlParams.coupon_code;
  let { shouldShowPoweredByCircle } = useOnboardingWhiteLabel(currentCommunity);
  if (couponCode) {
    shouldShowPoweredByCircle = false;
  }
  const isComingFromReactNativeWebview =
    window.webview?.isComingFromReactNativeWebview;

  const { title, subtitle, step } = determineTitleAndSubtitle(
    currentCommunity,
    promotion_details,
    localeScope,
  );

  const pageTitle = t(`${localeScope}.page_title`);

  useEffect(() => {
    setShouldShowForm(currentHash === "#email");
  }, [currentHash]);

  useEffect(() => {
    if (couponCode) {
      setShouldShowForm(true);
    }
  }, [couponCode]);

  return (
    <OnboardingLayout
      community={currentCommunity}
      title={title}
      subtitle={subtitle}
      step={step}
      isNarrow={!shouldShowForm}
      withCircleBranding={isEmpty(currentCommunity)}
      shouldShowPoweredByCircle={shouldShowPoweredByCircle}
      isVerticallyCenteredOnMobile
    >
      <PageTitle title={pageTitle} />
      <div>
        {shouldShowForm ? (
          <SignUpForm
            currentCommunity={currentCommunity}
            invitationToken={invitationToken}
            errors={errors}
          />
        ) : (
          <LoginOptions
            currentCommunity={currentCommunity}
            invitationToken={invitationToken}
            formUrl={authenticationAppRoutes.signUp({ params: urlParams })}
            variant="signup"
          />
        )}
      </div>
      {!isComingFromReactNativeWebview && (
        <div className="mt-6 flex flex-col items-center justify-center">
          {shouldShowForm && (
            <BackToOptionsLink
              url={authenticationAppRoutes.signUp({ params: urlParams })}
            />
          )}
          <AlreadyHaveAccountLink
            url={authenticationAppRoutes.signIn({ params: urlParams })}
          />
        </div>
      )}
    </OnboardingLayout>
  );
};

const determineTitleAndSubtitle = (
  currentCommunity,
  promotion_details,
  localeScope,
) => {
  let title = t(`${localeScope}.title.free_trial`, {
    days: promotion_details?.trial_days || 14,
  });
  let subtitle = t(`${localeScope}.subtitle`);
  let step;

  if (promotion_details?.skip_sign_up_funnel) {
    title = t(`${localeScope}.promotion.title`);
    subtitle = t(`${localeScope}.promotion.subtitle`);
    step = t(`${localeScope}.promotion.step_1`);
  } else if (currentCommunity) {
    title = t(`${localeScope}.title.join_community`, {
      community_name: currentCommunity.name,
    });
    subtitle = "";
  }

  return { title, subtitle, step };
};
